/**
 * The "Latest News" grid: the newest articles as cards with a category badge, the title (a link
 * to `/about-us/news-events/news/<categorySlug>/<slug>`) and the publish date as `MM/DD/YYYY`.
 *
 * Callers: `src/pages/about-us/news-events/index.tsx` (12 cards from `lg` up, 9 below) and
 * `src/pages/about-us/index.tsx` with `content.minimal` (4 and 3). Both pass the newest-first
 * list from `src/contentful/news/getContentfulNewsForSections.ts`. Every variant ends with a
 * link to `content.viewAllNewsURL` (`src/pages/about-us/news-events/news/index.tsx`); the full
 * variant also has one under the heading.
 *
 * The badge label and colour come from `./NewsArticle.tsx` → `MultiLingualNewsCategories`
 * (by `categorySlug`) and `NewsBadgeColor` (by the English `category` label).
 */
import React from "react";
import { NewsEventsStaticContent } from "../types";
import { Media } from "@ryerson/frontend.next";
import styled from "@emotion/styled";
import { css } from "@emotion/react";
import { useApplication } from "@ryerson/frontend.application";
import { useTheme } from "@ryerson/frontend.theme";
import { Typography } from "@ryerson/frontend.typography";
import { ButtonLink } from "@ryerson/frontend.navigation";
import { Grid, Row, Column } from "@ryerson/frontend.layout";
import { Badge } from "@ryerson/frontend.notification";
import { Link } from "@ryerson/frontend.navigation";
import Spacer from "@components/Shared/Spacer/Spacer";
import {
	NewsBadgeColor,
	MultiLingualNewsCategories,
	NewsCategories,
} from "@components/NewsEvents/News/NewsArticle";
import { ProcessedContentfulNewsForSectionsByLanguage } from "@contentful/news/types";

const NewsBlock = styled.div`
	${(props: any) => {
		const { theme } = props;
		return css`
			box-sizing: border-box;
			width: 100%;
			position: relative;

			@media only screen and (min-width: ${theme.breakpoints.lg}) {
				height: 320px;
				padding: 30px 0px 40px 37px;
				background-color: ${theme.colors.primary.background};
			}
			@media only screen and (max-width: ${theme.breakpoints.lg}) {
				height: 220px;
				padding: 17px 20px;
				background-color: ${theme.colors.secondary.background};
				margin-bottom: 20px;
			}
		`;
	}}
`;

type LatestNewsSectionProps = {
	content: NewsEventsStaticContent;
	contentfulNewsByLanguage: ProcessedContentfulNewsForSectionsByLanguage;
};

/** Renders the current language's newest articles; see the file header for the counts. */
const LatestNewsSection: React.FC<LatestNewsSectionProps> = ({
	content,
	contentfulNewsByLanguage,
}) => {
	const { theme } = useTheme();
	const {
		localization: { language },
	} = useApplication();

	const contentfulNewsForLanguage = contentfulNewsByLanguage[language];

	let displayedNews = content.minimal
		? contentfulNewsForLanguage.slice(0, 4)
		: contentfulNewsForLanguage.slice(0, 12);

	let displayedNewsMobile = content.minimal
		? contentfulNewsForLanguage.slice(0, 3)
		: contentfulNewsForLanguage.slice(0, 9);

	// "YYYY-MM-DD" -> "MM/DD/YYYY" in every language. With " 00:00:00" appended, engines that
	// accept the form read local midnight, so the stored day shows in every time zone (a bare
	// "YYYY-MM-DD" is UTC midnight, the day before west of UTC). The form isn't ISO 8601, so the
	// spec leaves its parsing to the engine. Same copy in `./AllNews.tsx` and `./NewsArticle.tsx`.
	const FormatDate = (date: any) => {
		let dateObject = new Date(date + " 00:00:00");
		let m: any = dateObject.getMonth() + 1;
		let d: any = dateObject.getDate();
		let y: any = dateObject.getFullYear();
		if (m < 10) m = "0" + m;
		if (d < 10) d = "0" + d;
		return m + "/" + d + "/" + y;
	};

	return (
		<>
			<Media greaterThanOrEqual="lg">
				<Typography
					variant="h2"
					type="secondary"
					css={css`
						margin-bottom: ${content.minimal ? "6px" : "31px"};
					`}
				>
					{content.latestNewsLabel}
				</Typography>

				{content.minimal ? (
					<></>
				) : (
					<ButtonLink
						label={content.viewAllNewsLabel}
						to={content.viewAllNewsURL}
						linkType="internal"
						type="secondary"
						size="sm"
					/>
				)}
				<Spacer
					css={css`
						height: 47px;
					`}
				/>
				<Grid>
					<Row>
						{displayedNews.map((news, index) => (
							<Column lg={6} gutter={5} key={news.slug}>
								<NewsBlock theme={theme}>
									<Badge
										badgeType="pill"
										size="sm"
										type="primary"
										color={NewsBadgeColor(news.category as NewsCategories)}
									>
										{MultiLingualNewsCategories[language][news.categorySlug]}
									</Badge>
									<Link
										key={index}
										to={
											"/about-us/news-events/news/" +
											encodeURI(news.categorySlug) +
											"/" +
											encodeURI(news.slug)
										}
									>
										<Typography
											variant="p"
											size="md"
											weight="bold"
											color={theme.colors.primary.header}
											css={css`
												padding-right: 55px;
											`}
										>
											{news.title}
										</Typography>
									</Link>

									<Typography
										variant="p"
										size="sm"
										color={theme.colors.primary.gray}
										css={css`
											position: absolute;
											bottom: 20px;
										`}
									>
										{FormatDate(news.publishDate)}
									</Typography>
								</NewsBlock>
							</Column>
						))}
					</Row>
				</Grid>
				<Spacer
					css={css`
						height: 47px;
					`}
				/>
				<ButtonLink
					label={content.viewAllNewsLabel}
					to={content.viewAllNewsURL}
					linkType="internal"
					type="secondary"
					size="sm"
				/>
			</Media>
			<Media lessThan="lg">
				{content.minimal ? (
					<></>
				) : (
					<>
						<Spacer
							css={css`
								height: 29px;
							`}
						/>
						<ButtonLink
							label={content.viewAllNewsLabel}
							to={content.viewAllNewsURL}
							linkType="internal"
							type="primary"
						/>
					</>
				)}
				<Spacer
					css={css`
						height: 29px;
					`}
				/>
				{displayedNewsMobile.map((news: any, index: number) => (
					<React.Fragment key={news.title}>
						<NewsBlock theme={theme}>
							<Badge
								badgeType="pill"
								size="lg"
								type="primary"
								color={NewsBadgeColor(news.category)}
								css={css`
									margin-left: -12px;
								`}
							>
								{MultiLingualNewsCategories[language][news.categorySlug]}
							</Badge>
							<Link
								key={index}
								to={
									"/about-us/news-events/news/" +
									encodeURI(news.categorySlug) +
									"/" +
									encodeURI(news.slug)
								}
							>
								<Typography
									variant="p"
									size="lg"
									weight="bold"
									color={theme.colors.primary.header}
								>
									{news.title}
								</Typography>
							</Link>

							<Typography
								variant="p"
								size="md"
								color={theme.colors.primary.gray}
								css={css`
									position: absolute;
									bottom: 20px;
								`}
							>
								{FormatDate(news.publishDate)}
							</Typography>
						</NewsBlock>
					</React.Fragment>
				))}
				<ButtonLink
					label={content.viewAllNewsLabel}
					to={content.viewAllNewsURL}
					linkType="internal"
					type="primary"
				/>
				<Spacer
					css={css`
						height: 66px;
					`}
				/>
			</Media>
		</>
	);
};

export default LatestNewsSection;
